import React from 'react';

import { useTranslation } from '../../../../hooks/useTranslation/useTranslation';
import { toCssUrl } from '../../../../services/urlSafety';
import styles from './GameHero.module.css';
import { GameHeroProps } from './GameHero.types';

export const GameHero: React.FC<GameHeroProps> = ({ game }) => {
  const { t } = useTranslation();

  return (
    <section className={styles.hero} aria-labelledby="game-title">
      <div
        className={styles.heroBackdrop}
        style={{ backgroundImage: toCssUrl(game.heroBannerUrl) }}
        aria-hidden="true"
      />
      <div className={styles.heroOverlay} aria-hidden="true" />

      <div className={styles.heroContent}>
        {/* Badges Row */}
        <div className={styles.badgesRow}>
          <span className={styles.badgeStudio}>{t.games.hero.originalTitle}</span>
          <span className={styles.badgeStatus}>
            <span className={styles.pulsingDot} aria-hidden="true" />
            {t.games.hero.inDevelopment}
          </span>
        </div>

        {/* Title and Tagline */}
        <div className={styles.titleArea}>
          <h1 id="game-title" className={styles.mainTitle}>
            {game.title}
          </h1>
          <p className={styles.tagline}>{game.tagline}</p>
        </div>

        {/* Feature Pills */}
        <div className={styles.featurePills}>
          <div className={styles.featurePill}>
            <span className={styles.pillIcon} aria-hidden="true">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            </span>
            <span>{t.games.hero.flatScreenLabel}</span>
          </div>

          <div className={styles.featurePill}>
            <span className={styles.pillIcon} aria-hidden="true">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </span>
            <span>{t.games.hero.engineLabel}</span>
          </div>

          <div className={styles.featurePill}>
            <span className={styles.pillIcon} aria-hidden="true">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="2" y="6" width="20" height="12" rx="3" />
                <circle cx="8" cy="12" r="2" />
                <circle cx="16" cy="12" r="2" />
              </svg>
            </span>
            <span>{t.games.hero.vrSupportLabel}</span>
          </div>
        </div>

        {/* Metadata Grid */}
        <div className={styles.metadataGrid}>
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>{t.games.hero.developerLabel}</span>
            <span className={styles.metaValue}>{game.developer}</span>
          </div>

          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>{t.games.hero.releaseTargetLabel}</span>
            <span className={styles.metaValue}>{game.releaseDateText}</span>
          </div>

          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>{t.games.hero.genreLabel}</span>
            <span className={styles.metaValue}>{game.genre.join(', ')}</span>
          </div>

          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>{t.games.hero.platformsLabel}</span>
            <span className={styles.metaValue}>{game.platforms.join(' • ')}</span>
          </div>
        </div>
      </div>
    </section>
  );
};
